/**
 * InlineCallout Component
 * 
 * A prominent notification for highlighting important information.
 * Features inline title/subtitle layout with optional action buttons.
 * 
 * @example
 * ```tsx
 * <InlineCallout
 *   id="callout-1"
 *   kind="info"
 *   title="Pro Tip"
 *   subtitle="You can use keyboard shortcuts to navigate faster"
 *   timestamp={Date.now()}
 *   variant="translucent"
 * />
 * ```
 */
import React from 'react';
import { Close } from '@core/icons-react';
import {
  ErrorFilled,
  CheckmarkFilled,
  WarningFilled,
  InformationFilled
} from '@core/icons-react';
import { Tag, seg } from '../../../../tags/Tag';
import type { BaseNotificationProps } from '../../base/types';

// Icon mapping for different notification kinds
const iconMap = {
  error: ErrorFilled,
  success: CheckmarkFilled,
  warning: WarningFilled,
  info: InformationFilled,
};

export interface InlineCalloutProps extends BaseNotificationProps {
  /** Whether to show a background (default true for callouts) */
  showBackground?: boolean;
  
  /** Button variant - only used if children is provided */
  buttonVariant?: 'icon' | 'text';
  
  /** Icon for the button */
  buttonIcon?: React.ReactNode;
  
  /** Click handler for the action button */
  onButtonClick?: () => void;
  
  /** Aria label for icon-only buttons */
  buttonAriaLabel?: string;
  
  /** Visual variant: translucent (default) or solid (layer1-based) */
  variant?: 'translucent' | 'solid';
}

export const InlineCallout: React.FC<InlineCalloutProps> = ({
  id,
  kind,
  title,
  subtitle,
  children,
  hideCloseButton = true,
  lowContrast = true,
  onClose,
  onCloseButtonClick,
  statusIconDescription,
  showBackground = true,
  buttonVariant = 'text',
  buttonIcon,
  onButtonClick,
  buttonAriaLabel,
  variant = 'translucent',
  ...props
}) => {
  const baseClass = 'notification';
  const IconComponent = iconMap[kind as keyof typeof iconMap] || iconMap.info;
  
  const classNames = [
    baseClass,
    `${baseClass}--callout`,
    `${baseClass}--${kind}`,
    `${baseClass}--variant-${variant}`,
    lowContrast && `${baseClass}--low-contrast`,
    hideCloseButton && `${baseClass}--hide-close-button`,
    !showBackground && `${baseClass}--callout-no-bg`,
  ].filter(Boolean).join(' ');
  
  const handleCloseButtonClick = (event: React.MouseEvent) => {
    if (onCloseButtonClick) {
      onCloseButtonClick(event as any);
    }
    if (onClose) {
      onClose(event as any);
    }
  };
  
  // Render children as action Tag if provided
  const renderActionButton = () => {
    if (!children) return null;
    
    if (buttonVariant === 'icon') {
      return (
        <Tag
          template="action-sm"
          onClick={onButtonClick}
          title={buttonAriaLabel || 'Action'}
        >
          {buttonIcon}
        </Tag>
      );
    }
    
    if (buttonVariant === 'text') {
      const label = typeof children === 'string' ? children : buttonAriaLabel;
      return (
        <Tag
          template="action-sm"
          segments={label ? [seg.text(label, { size: 'md' })] : []}
          onClick={onButtonClick}
        />
      );
    }
    
    return null;
  };
  
  return (
    <div
      className={classNames}
      role="status"
      aria-labelledby={title ? `${id}-title` : undefined}
      aria-describedby={subtitle ? `${id}-subtitle` : undefined}
    >
      {/* Icon */}
      {IconComponent && (
        <div className={`${baseClass}__icon-container`}>
          <IconComponent 
            className={`${baseClass}__icon`}
            size={14}
            aria-label={statusIconDescription || `${kind} icon`}
          />
        </div>
      )}

      {/* Content */}
      <div className={`${baseClass}__content`}>
        {title && (
          <div id={`${id}-title`} className={`${baseClass}__title`}>
            {title}
          </div>
        )}
        
        {subtitle && (
          <div 
            id={`${id}-subtitle`} 
            className={`${baseClass}__subtitle`}
            title={subtitle}
          >
            {subtitle}
          </div>
        )}
      </div>

      {/* Actions container - button on left, close on right */}
      <div className={`${baseClass}__actions-container`}>
        {/* Action button (from children) */}
        {renderActionButton()}
        
        {/* Close button */}
        {!hideCloseButton && (
          <Tag
            template="action-sm"
            segments={[seg.icon(Close, { size: 16 })]}
            onClick={handleCloseButtonClick}
            title="Close notification"
          />
        )}
      </div>
    </div>
  );
};

